import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Star, Languages, Briefcase, Clock3, Search, X } from "lucide-react";
import InitialsAvatar from "@/components/InitialsAvatar";

const PRICE_BUCKETS = [
  { id: "lt-500", label: "Under ₹500", test: (p) => p > 0 && p < 500 },
  { id: "500-1000", label: "₹500–1,000", test: (p) => p >= 500 && p <= 1000 },
  { id: "1000-2000", label: "₹1,000–2,000", test: (p) => p > 1000 && p <= 2000 },
  { id: "gt-2000", label: "Over ₹2,000", test: (p) => p > 2000 },
  { id: "free", label: "Pay what feels right", test: (p) => p === 0 },
];

const EXPERIENCE_BUCKETS = [
  { id: "fresher", label: "Fresher / new", test: (y) => y <= 0 },
  { id: "1-3", label: "1–3 years", test: (y) => y >= 1 && y <= 3 },
  { id: "3-10", label: "3–10 years", test: (y) => y > 3 && y <= 10 },
  { id: "10-plus", label: "10+ years", test: (y) => y > 10 },
];

export default function Directory() {
  const [therapists, setTherapists] = useState([]);
  const [query, setQuery] = useState("");
  const [selectedLangs, setSelectedLangs] = useState([]);
  const [selectedSpecs, setSelectedSpecs] = useState([]);
  const [selectedPrice, setSelectedPrice] = useState(null);
  const [selectedExp, setSelectedExp] = useState(null);

  useEffect(() => {
    api.get("/therapists").then(({ data }) => setTherapists(data));
  }, []);

  // Unique facets, derived from the data we already have
  const allLanguages = useMemo(() => {
    const s = new Set();
    therapists.forEach((t) => (t.languages || []).forEach((l) => s.add(l)));
    return Array.from(s).sort();
  }, [therapists]);
  const allSpecialties = useMemo(() => {
    const s = new Set();
    therapists.forEach((t) => (t.specialties || []).forEach((sp) => s.add(sp)));
    return Array.from(s).sort();
  }, [therapists]);

  const toggle = (list, setList) => (v) =>
    setList(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return therapists.filter((t) => {
      if (q) {
        const haystack = [
          t.name,
          t.title,
          t.bio,
          ...(t.specialties || []),
          ...(t.languages || []),
          t.experience,
        ].filter(Boolean).join(" ").toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      if (selectedLangs.length && !selectedLangs.every((l) => (t.languages || []).includes(l))) return false;
      if (selectedSpecs.length && !selectedSpecs.some((s) => (t.specialties || []).includes(s))) return false;
      if (selectedPrice) {
        const bucket = PRICE_BUCKETS.find((b) => b.id === selectedPrice);
        if (bucket && !bucket.test(Number(t.session_price || 0))) return false;
      }
      if (selectedExp) {
        const bucket = EXPERIENCE_BUCKETS.find((b) => b.id === selectedExp);
        if (bucket && !bucket.test(Number(t.experience_years || 0))) return false;
      }
      return true;
    });
  }, [therapists, query, selectedLangs, selectedSpecs, selectedPrice, selectedExp]);

  const activeFilterCount =
    (query ? 1 : 0) + selectedLangs.length + selectedSpecs.length + (selectedPrice ? 1 : 0) + (selectedExp ? 1 : 0);

  const clearAll = () => {
    setQuery("");
    setSelectedLangs([]);
    setSelectedSpecs([]);
    setSelectedPrice(null);
    setSelectedExp(null);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20" data-testid="page-directory">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
        <div>
          <div className="text-xs tracking-[0.25em] uppercase text-mindora-red mb-4">
            <span className="editorial-rule" />Counselors & psychologists
          </div>
          <h1 className="font-serif text-5xl lg:text-6xl text-mindora-ink leading-tight max-w-3xl">
            When stories aren't enough,<br /><em className="italic text-mindora-red">talk to someone trained to listen.</em>
          </h1>
          <p className="mt-4 text-mindora-muted leading-relaxed max-w-2xl">
            Vetted clinicians offering one-on-one sessions and group workshops. Book directly inside Mindora.
          </p>
        </div>
        <Link
          to="/directory/apply"
          data-testid="directory-apply-cta"
          className="self-start lg:self-end inline-flex items-center gap-2 rounded-full border border-mindora-red text-mindora-red px-5 py-2.5 text-sm hover:bg-mindora-red hover:text-mindora-bg transition-colors"
        >
          <Briefcase size={14} />
          Are you a counselor? Apply to join
        </Link>
      </div>

      {/* Filters */}
      <div className="rounded-2xl border border-mindora-line bg-mindora-panel p-5 mt-2" data-testid="directory-filters">
        <div className="relative mb-4">
          <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-mindora-muted" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, language, specialty…"
            data-testid="directory-search"
            className="w-full rounded-full border border-mindora-line bg-mindora-bg pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-mindora-red"
          />
        </div>

        <FilterRow label="Language" testId="filter-languages">
          {allLanguages.map((l) => (
            <Chip key={l} active={selectedLangs.includes(l)} onClick={() => toggle(selectedLangs, setSelectedLangs)(l)} testId={`filter-lang-${l}`}>
              {l}
            </Chip>
          ))}
        </FilterRow>

        <FilterRow label="Specialty" testId="filter-specialties">
          {allSpecialties.map((s) => (
            <Chip key={s} active={selectedSpecs.includes(s)} onClick={() => toggle(selectedSpecs, setSelectedSpecs)(s)} testId={`filter-spec-${s}`}>
              {s}
            </Chip>
          ))}
        </FilterRow>

        <FilterRow label="Price" testId="filter-price">
          {PRICE_BUCKETS.map((b) => (
            <Chip key={b.id} active={selectedPrice === b.id} onClick={() => setSelectedPrice(selectedPrice === b.id ? null : b.id)} testId={`filter-price-${b.id}`}>
              {b.label}
            </Chip>
          ))}
        </FilterRow>

        <FilterRow label="Experience" testId="filter-experience" last>
          {EXPERIENCE_BUCKETS.map((b) => (
            <Chip key={b.id} active={selectedExp === b.id} onClick={() => setSelectedExp(selectedExp === b.id ? null : b.id)} testId={`filter-exp-${b.id}`}>
              {b.label}
            </Chip>
          ))}
        </FilterRow>

        {activeFilterCount > 0 && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-mindora-line">
            <div className="text-xs text-mindora-muted" data-testid="filter-count">
              Showing <b className="text-mindora-ink">{filtered.length}</b> of {therapists.length} · {activeFilterCount} filter{activeFilterCount > 1 ? "s" : ""} active
            </div>
            <button
              onClick={clearAll}
              data-testid="filters-clear"
              className="inline-flex items-center gap-1.5 text-xs text-mindora-red hover:opacity-80"
            >
              <X size={12} /> Clear all
            </button>
          </div>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="mt-14 text-center text-mindora-muted italic" data-testid="directory-empty">
          No counsellors match those filters. Try clearing one or two.
        </div>
      ) : (
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7" data-testid="directory-grid">
          {filtered.map((t) => (
          <article
            key={t.id}
            className="rounded-3xl border border-mindora-line bg-mindora-panel overflow-hidden shadow-clay hover:shadow-clayHover transition-all duration-500"
            data-testid={`directory-card-${t.id}`}
          >
            <div className="aspect-[5/4] overflow-hidden">
              {t.photo_url ? (
                <img src={t.photo_url} alt={t.name} className="w-full h-full object-cover" />
              ) : (
                <InitialsAvatar name={t.name} testId={`directory-avatar-${t.id}`} className="w-full h-full" />
              )}
            </div>
            <div className="p-6">
              <div className="flex items-center justify-between">
                <h3 className="font-serif text-2xl text-mindora-ink leading-tight">{t.name}</h3>
                <span className="text-xs text-mindora-red flex items-center gap-1">
                  <Star size={12} fill="#A73C35" /> {t.rating}
                </span>
              </div>
              <div className="text-xs uppercase tracking-[0.18em] text-mindora-red mt-1">{t.title}</div>
              {t.experience && (
                <div className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] tracking-wide text-mindora-muted">
                  <Clock3 size={11} /> {t.experience} of practice
                </div>
              )}
              <p className="mt-3 text-sm text-mindora-muted leading-relaxed line-clamp-3">{t.bio}</p>

              <div className="mt-4 flex flex-wrap gap-1.5">
                {t.specialties.slice(0, 4).map((s) => (
                  <span key={s} className="text-[10px] uppercase tracking-[0.16em] text-mindora-ink/70 border border-mindora-line rounded-full px-2.5 py-0.5">{s}</span>
                ))}
              </div>

              <div className="flex items-center justify-between mt-5 pt-5 border-t border-mindora-line">
                <span className="text-xs text-mindora-muted flex items-center gap-1.5">
                  <Languages size={12} /> {t.languages.join(", ")}
                </span>
                <span className="font-serif text-xl text-mindora-ink" data-testid={`directory-price-${t.id}`}>
                  {t.session_price_display || `₹${t.session_price}`}
                </span>
              </div>

              <Link
                to={`/directory/${t.id}`}
                data-testid={`directory-book-link-${t.id}`}
                className="block text-center mt-5 btn-primary rounded-full px-5 py-2.5 text-sm tracking-wide"
              >
                Book a session
              </Link>
            </div>
          </article>
        ))}
        </div>
      )}

      {/* Apply-to-join section */}
      <section className="mt-24 rounded-3xl bg-mindora-ink text-mindora-bg p-10 lg:p-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center" data-testid="directory-apply-section">
        <div className="lg:col-span-8">
          <div className="text-xs tracking-[0.25em] uppercase text-mindora-clay mb-4">
            <span className="inline-block w-7 h-px bg-mindora-clay mr-3 align-middle" />For clinicians
          </div>
          <h2 className="font-serif text-4xl lg:text-5xl leading-tight">
            Are you a psychologist, counselor, or therapist?<br />
            <em className="italic text-mindora-clay">Practice on Mindora.</em>
          </h2>
          <p className="mt-5 text-mindora-bg/75 leading-relaxed max-w-2xl">
            We're growing the network slowly and carefully. If you work with young people and believe
            in story-first, anonymity-respecting care, we'd love to learn more about you.
          </p>
        </div>
        <div className="lg:col-span-4 flex lg:justify-end">
          <Link
            to="/directory/apply"
            data-testid="directory-apply-cta-bottom"
            className="inline-block bg-mindora-bg text-mindora-ink rounded-full px-7 py-3.5 text-sm tracking-wide hover:bg-mindora-clay hover:text-mindora-ink transition-colors"
          >
            Submit an application →
          </Link>
        </div>
      </section>
    </div>
  );
}

function FilterRow({ label, children, testId, last = false }) {
  return (
    <div className={`flex items-start gap-3 ${last ? "" : "mb-2.5"}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-mindora-muted shrink-0 mt-1.5 w-20">{label}</div>
      <div className="flex flex-wrap gap-1.5">{children}</div>
    </div>
  );
}

function Chip({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`text-[11px] rounded-full border px-3 py-1 transition-colors ${
        active
          ? "bg-mindora-red text-mindora-bg border-mindora-red"
          : "border-mindora-line text-mindora-ink/70 hover:border-mindora-red hover:text-mindora-red"
      }`}
    >
      {children}
    </button>
  );
}
